import { useState, useMemo, useCallback } from 'react';

type UseCustomItemAdditionProps<Item> = {
  /** Current search input value from the user */
  searchInputValue: string;
  /** Optional function to transform search input into a new item */
  addCustomItem?: (inputValue: string) => Item;
  /** The base items array (propItems) */
  propItems: Item[];
  /** Function to convert an item to its string value for comparison */
  itemToStringValue: (item: Item) => string;
  /** Callback when a custom item is added */
  onItemAdded: (newItem: Item) => void;
};

type UseCustomItemAdditionReturn<Item> = {
  /** Combined array of original items and custom items */
  items: Item[];
  /** Whether the current search input would create a unique item */
  isItemUniqueValue: boolean;
  /** Function to handle adding a custom item */
  handleAddCustomItem: () => void;
};

/**
 * Hook to manage custom item addition logic for autocomplete components.
 * Handles state management, uniqueness validation, and item creation.
 */
export const useCustomItemAddition = <Item>({
  searchInputValue,
  addCustomItem,
  propItems,
  itemToStringValue,
  onItemAdded,
}: UseCustomItemAdditionProps<Item>): UseCustomItemAdditionReturn<Item> => {
  const [customItems, setCustomItems] = useState<Item[]>([]);

  // Merge prop items with custom items
  const items = useMemo(
    () => [...customItems, ...propItems],
    [propItems, customItems]
  );

  // Check if the current search input would create a unique item
  const isItemUniqueValue: boolean = useMemo(() => {
    if (!addCustomItem || searchInputValue === '') return false;

    const currentValues = items.map((item) => itemToStringValue(item));
    const potentialNewValue = itemToStringValue(
      addCustomItem(searchInputValue)
    );

    return !currentValues.includes(potentialNewValue);
  }, [items, itemToStringValue, addCustomItem, searchInputValue]);

  // Handle adding a custom item
  const handleAddCustomItem = useCallback(() => {
    if (!addCustomItem || !searchInputValue || !isItemUniqueValue) return;

    const newItem = addCustomItem(searchInputValue);
    setCustomItems((prev) => [...prev, newItem]);
    onItemAdded(newItem);
  }, [addCustomItem, searchInputValue, isItemUniqueValue, onItemAdded]);

  return {
    items,
    isItemUniqueValue,
    handleAddCustomItem,
  };
};
